import classNames from 'classnames'

import Card from '@/components/Card/Card'
import CardHover from '@/components/CardHover/CardHover'
import ContentHint from '@/components/CardLists/CardView/ContentHint'
import LegalityChip from '@/components/CardLists/ComboView/LegalityChip/LegalityChip'
import CardCountIcon from '@/components/Combo/CardCountIcon/CardCountIcon'
import CornerBackgroundImage from '@/components/Combo/CornerBackgroundImage/CornerBackgroundImage'
import SideScroller from '@/components/Combo/SideScroller/SideScroller'
import Icon from '@/components/Icon/Icon'
import Mana, { ManaType } from '@/components/Mana/Mana'
import { useAds } from '@/contexts/ads'
import { CardListWithComboType, PageCardType } from '@/types/card'
import { comboTierState } from '@/utils/combos'
import { artCrop } from '@/utils/images'

import styles from './ComboView.module.scss'

const NUMBER_BEFORE_ADS = 10

type Props = {
  cardLists: CardListWithComboType[]
  comboCard?: PageCardType // If the page is for combos that include a card we only send the card once, so we append it to every combo
  textView?: boolean
}

const ComboView = ({ cardLists, comboCard, textView }: Props) => {
  const { loadAds } = useAds()

  return (
    <div className='d-flex flex-wrap gap-4 p-3'>
      {cardLists.map((cardList, index) => {
        const showAd = index % NUMBER_BEFORE_ADS === 0 && index !== 0 && loadAds
        return (
          <>
            {showAd && (
              <div className={styles.hint}>
                <ContentHint numCols={1} tall wide />
              </div>
            )}
            <div className={styles.comboEntry} key={index}>
              {cardList.combo.cardIds[0] && (
                <CornerBackgroundImage image={artCrop(cardList.combo.cardIds[0])} position='topLeft' size={200} />
              )}
              {cardList.combo.cardIds[1] && (
                <CornerBackgroundImage image={artCrop(cardList.combo.cardIds[1])} position='bottomRight' size={200} />
              )}
              {textView && (
                <div className='d-flex flex-column align-items-center text-center m-4'>
                  {cardList.cardviews.map((card, i) => (
                    <div className={styles.card} key={i}>
                      <CardHover card={card} />
                    </div>
                  ))}
                  {comboCard && (
                    <div className={styles.card}>
                      <CardHover card={comboCard} />
                    </div>
                  )}
                </div>
              )}

              {!textView && (
                <div className={styles.cardContainer}>
                  <SideScroller disableSideWheel narrow>
                    {cardList.cardviews.map((card, i) => (
                      <div className={styles.card} key={i}>
                        <Card card={card} scalable />
                      </div>
                    ))}
                    {comboCard && (
                      <div className={styles.card}>
                        <Card card={comboCard} scalable />
                      </div>
                    )}
                  </SideScroller>
                </div>
              )}

              <div className='d-flex justify-content-center align-items-center'>
                <div
                  className={classNames(
                    'd-flex flex-column align-items-center text-center flex-grow-1',
                    styles.splitStatItem,
                  )}
                >
                  <CardCountIcon count={cardList.cardviews.length + (comboCard ? 1 : 0)} />
                  {cardList.cardviews.length + (comboCard ? 1 : 0)} card combo
                  <br />
                  <span>
                    {!!cardList.combo.nonCardPrerequisiteCount && (
                      <>
                        <a href={cardList.href}>
                          +{cardList.combo.nonCardPrerequisiteCount} other prerequisite
                          <span>{cardList.combo.nonCardPrerequisiteCount > 1 ? 's' : ''}</span>
                        </a>
                        <br />
                      </>
                    )}
                  </span>
                </div>

                <div
                  className={classNames(
                    'd-flex flex-column align-items-center text-center flex-grow-1',
                    styles.splitStatItem,
                  )}
                >
                  <Icon className='fa-2x' icon='chartSimple' />
                  <span>
                    In <b>{cardList.combo.count}</b> decks
                  </span>
                  {cardList.combo.percentage && (
                    <span>
                      ({cardList.combo.percentage.toFixed(2)}% of {cardList.combo.maxCount})
                    </span>
                  )}
                </div>
              </div>

              <div className='d-flex flex-column align-items-center text-center m-4'>
                <Icon className='fa-2x' icon='infinity' />
                {cardList.combo.results.length <= 3 &&
                  cardList.combo.results.map((result, i) => <div key={i}>{result}</div>)}
                {cardList.combo.results.length > 3 && (
                  <div>
                    {cardList.combo.results.slice(0, 2).map((result, i) => (
                      <div key={i}>{result}</div>
                    ))}
                    <a href={cardList.href}>+{cardList.combo.results.length - 2} more results</a>
                  </div>
                )}
              </div>

              <div className='d-flex gap-2 flex-grow-1 justify-content-end flex-column m-1'>
                <div className={styles.subtext}>
                  <b>&#123;&nbsp;&#125;</b> Commander Brackets:
                </div>
                <div className='d-flex gap-2 flex-wrap'>
                  <LegalityChip
                    bracket='1'
                    href={cardList.href || ''}
                    state={comboTierState(cardList.combo, cardList.cardviews).lowTierState}
                  />
                  <LegalityChip
                    bracket='2'
                    href={cardList.href || ''}
                    state={comboTierState(cardList.combo, cardList.cardviews).lowTierState}
                  />
                  <LegalityChip
                    bracket='3'
                    href={cardList.href || ''}
                    state={comboTierState(cardList.combo, cardList.cardviews).midTierState}
                  />
                  <LegalityChip bracket='4' href={cardList.href || ''} state='success' />
                  <LegalityChip bracket='5' href={cardList.href || ''} state='success' />
                </div>
              </div>

              <div className={styles.footer}>
                <a href={cardList.href}>
                  <button className={styles.viewComboButton}>View combo details</button>
                </a>

                <div>
                  {cardList.combo.colors.split('').map((color, i) => (
                    <Mana color={color as ManaType} key={i} />
                  ))}
                </div>
              </div>
            </div>
          </>
        )
      })}

      {Array.from({ length: 10 }, (_, i) => (
        <div className={styles.trailingBlankSpace} key={i} />
      ))}
    </div>
  )
}

export default ComboView
